import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} acceptButton
 * @property {HTMLButtonElement} declineButton
 * @property {HTMLButtonElement} manageButton
 * @property {HTMLElement} preferences
 * @property {HTMLInputElement} analyticsToggle
 * @property {HTMLInputElement} marketingToggle
 * @property {HTMLInputElement} preferencesToggle
 */

export class CookieConsentBanner extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initPrivacyApi();
  }

  #initPrivacyApi() {
    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) {
            console.error('Failed to load consent tracking API', error);
            return;
          }
          this.#checkConsent();
        }
      );
    } else {
      // Fallback: wait for Shopify to load
      document.addEventListener('DOMContentLoaded', () => {
        if (window.Shopify && window.Shopify.loadFeatures) {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error) return;
              this.#checkConsent();
            }
          );
        }
      });
    }
  }

  #checkConsent() {
    const privacy = window.Shopify.customerPrivacy;
    if (!privacy) return;

    if (this.#shouldShowBanner(privacy)) {
      this.#showBanner();
    }
  }

  /**
   * @param {Object} privacy - Shopify customerPrivacy object
   * @returns {boolean}
   */
  #shouldShowBanner(privacy) {
    if (typeof privacy.shouldShowBanner === 'function') {
      return privacy.shouldShowBanner();
    }
    const consent = privacy.currentVisitorConsent();
    if (!consent) return true;
    return consent.analytics === '' && consent.marketing === '';
  }

  #showBanner() {
    this.removeAttribute('hidden');
    // Trigger reflow before adding visible attribute for animation
    this.offsetHeight;
    this.setAttribute('visible', '');
  }

  #hideBanner() {
    this.removeAttribute('visible');
    this.addEventListener(
      'transitionend',
      () => {
        this.setAttribute('hidden', '');
      },
      { once: true }
    );
  }

  /** @param {Event} _event */
  acceptAll(_event) {
    const privacy = window.Shopify.customerPrivacy;
    if (!privacy) return;

    privacy.setTrackingConsent(
      {
        analytics: true,
        marketing: true,
        preferences: true,
        sale_of_data: true,
      },
      () => this.#hideBanner()
    );
  }

  /** @param {Event} _event */
  togglePreferences(_event) {
    const prefs = this.refs.preferences;
    const isHidden = prefs.hasAttribute('hidden');
    if (isHidden) {
      prefs.removeAttribute('hidden');
    } else {
      prefs.setAttribute('hidden', '');
    }
  }

  /** @param {Event} _event */
  savePreferences(_event) {
    const privacy = window.Shopify.customerPrivacy;
    if (!privacy) return;

    privacy.setTrackingConsent(
      {
        analytics: this.refs.analyticsToggle.checked,
        marketing: this.refs.marketingToggle.checked,
        preferences: this.refs.preferencesToggle.checked,
        sale_of_data: this.refs.marketingToggle.checked,
      },
      () => this.#hideBanner()
    );
  }

  /** @param {Event} _event */
  declineAll(_event) {
    const privacy = window.Shopify.customerPrivacy;
    if (!privacy) return;

    privacy.setTrackingConsent(
      {
        analytics: false,
        marketing: false,
        preferences: false,
        sale_of_data: false,
      },
      () => this.#hideBanner()
    );
  }
}

if (!customElements.get('cookie-consent-banner')) {
  customElements.define('cookie-consent-banner', CookieConsentBanner);
}
